// API client del auxiliar contable por animal (Sprint 2 2026-05-17).
//
// Endpoint:
//   GET /api/accounts/:accountId/chart/accounts/:chartAccountId/auxiliary
//
// RBAC en BE: owner/admin/viewer leen; operator → 404. En este client no
// hacemos guard adicional — confiamos en el server response. La UI puede
// mostrar/ocultar la entrada según rol vía useCurrentAccount.

import { api } from "@/common/lib/api";
import { useQuery } from "@tanstack/react-query";

export type AuxiliaryEntry = {
  id: string;
  animalId: string;
  animalTag: string;
  animalName: string | null;
  sourceEventId: string;
  journalExportId: string;
  journalExportPeriodStart: string; // YYYY-MM-DD
  journalExportPeriodEnd: string;
  occurredAt: string; // ISO
  debitCents: number;
  creditCents: number;
  description: string;
};

export type ListAuxiliaryResponse = {
  entries: AuxiliaryEntry[];
  totals: { debitCents: number; creditCents: number };
};

export type AuxiliaryFilters = {
  animalId?: string;
  fromDate?: string; // YYYY-MM-DD
  toDate?: string;
  exportId?: string;
  limit?: number;
  offset?: number;
};

export const auxiliaryKey = (
  accountId: string,
  chartAccountId: string,
  filters: AuxiliaryFilters,
) => ["auxiliary", accountId, chartAccountId, filters] as const;

function buildQueryString(filters: AuxiliaryFilters): string {
  const params = new URLSearchParams();
  if (filters.animalId) params.set("animalId", filters.animalId);
  if (filters.fromDate) params.set("fromDate", filters.fromDate);
  if (filters.toDate) params.set("toDate", filters.toDate);
  if (filters.exportId) params.set("exportId", filters.exportId);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.offset !== undefined) params.set("offset", String(filters.offset));
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function useAuxiliaryEntries(
  accountId: string,
  chartAccountId: string | null,
  filters: AuxiliaryFilters,
) {
  return useQuery({
    queryKey: auxiliaryKey(accountId, chartAccountId ?? "", filters),
    queryFn: () =>
      api.get<ListAuxiliaryResponse>(
        `/accounts/${accountId}/chart/accounts/${chartAccountId}/auxiliary${buildQueryString(
          filters,
        )}`,
      ),
    enabled: Boolean(accountId && chartAccountId),
    staleTime: 30_000,
  });
}
